import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { T } from "../lib/i18n";
import { dbLoadLeaderboard } from "../lib/db";
import { startPolling } from "../lib/net";
import { useVersionCheck } from "../lib/version";
import { useTabSleep } from "../lib/sleep";

// ─────────────────────────────────────────
//  LIVE LEADERBOARD
// ─────────────────────────────────────────
function LeaderboardScreen({ leaderboardId, lang, theme, setTheme, G }) {
  const [results, setResults] = useState([]);
  // Somnar efter en halvtimme som dold flik (se useTabSleep), aldrig synlig.
  const { asleep, hasSlept } = useTabSleep();
  useVersionCheck(undefined, asleep);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const t = T[lang];

  async function fetchResults() {
    // Anonymous reads of the sessions table are blocked by RLS — resolve the
    // leaderboard code through the sanitized RPC instead
    const { data: sessionData, error: sErr } = await supabase
      .rpc("get_session_for_code", { code: leaderboardId });

    if (sErr) {
      // Transient (network) error — keep whatever we already show and retry on
      // the next poll instead of flashing "not found".
      setLoading(false);
      return;
    }
    if (!sessionData?.id) {
      setError(t.dashNotFound);
      setLoading(false);
      return;
    }
    setSessionId(sessionData.id);
    setError(null);

    // Sanitized progress table — scores only, no attempts
    const { rows, error: rErr } = await dbLoadLeaderboard(sessionData.id);
    if (!rErr) { setResults(rows); setLastUpdate(Date.now()); }
    setLoading(false);
  }

  useEffect(() => {
    if (asleep) return undefined;
    if (!hasSlept) fetchResults();
    // Långsam reserv till realtid; pausar när fliken är dold.
    return startPolling(async () => { await fetchResults(); }, { interval: 30000, report: false, immediate: hasSlept });
  }, [leaderboardId, asleep, hasSlept]);

  // Realtime: update the leaderboard the moment a group's result changes
  useEffect(() => {
    if (!sessionId || asleep) return;
    let debounce = null;
    const ch = supabase.channel(`lb-${sessionId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "group_progress", filter: `session_id=eq.${sessionId}` }, () => {
        clearTimeout(debounce);
        debounce = setTimeout(fetchResults, 250);
      })
      .subscribe();
    return () => { clearTimeout(debounce); supabase.removeChannel(ch); };
  }, [sessionId, asleep]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: G.bg, display: "flex", alignItems: "center", justifyContent: "center", color: G.text }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 18, color: G.muted }}>{t.loading}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: G.bg, display: "flex", alignItems: "center", justifyContent: "center", color: G.text }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 20, color: G.red, marginBottom: 10 }}>{error}</div>
        </div>
      </div>
    );
  }

  const maxScore = results.length > 0 ? results[0].max_score : 0;

  return (
    <div style={{ minHeight: "100vh", background: G.bg, fontFamily: "'DM Sans',sans-serif", color: G.text }}>
      {/* Header med tema-knapp */}
      <div style={{ background: G.surface, borderBottom: `2px solid ${G.border}`, padding: "20px 40px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontSize: 42, fontWeight: 700, color: G.gold, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "-0.02em" }}>
            🏆 {t.liveLeaderboard}
          </h1>
        </div>
        <button 
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          style={{ background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 12, color: G.muted, padding: "8px 14px", cursor: "pointer", display: "flex", alignItems: "center" }}>
          {theme === "dark"
            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          }
        </button>
      </div>

      {/* Resultat */}
      <div style={{ padding: "40px", maxWidth: 1200, margin: "0 auto" }}>
        {results.length === 0 ? (
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 16, padding: 60, textAlign: "center" }}>
            <div style={{ fontSize: 20, color: G.muted, marginBottom: 10 }}>{t.noScoresYet}</div>
            <div style={{ fontSize: 14, color: G.muted, fontFamily: "'DM Mono',monospace" }}>{t.updatingLive}</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {results.map((r, i) => {
              const percentage = maxScore > 0 ? (r.total / maxScore) * 100 : 0;
              const isFirst = i === 0;
              const isTop3 = i < 3;
              
              return (
                <div 
                  key={i}
                  style={{
                    background: isFirst ? `linear-gradient(135deg, ${G.gold}22 0%, ${G.surface} 100%)` : G.surface,
                    border: `2px solid ${isFirst ? G.gold : isTop3 ? G.goldDim : G.border}`,
                    borderRadius: 16,
                    padding: "24px 32px",
                    display: "flex",
                    alignItems: "center",
                    gap: 24,
                    transition: "all 0.3s ease"
                  }}
                >
                  {/* Rank */}
                  <div style={{
                    fontSize: isFirst ? 48 : isTop3 ? 36 : 28,
                    fontWeight: 700,
                    color: isFirst ? G.gold : isTop3 ? G.goldDim : G.muted,
                    minWidth: isFirst ? 70 : 60,
                    textAlign: "center",
                    fontFamily: "'Plus Jakarta Sans', sans-serif"
                  }}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                  </div>

                  {/* Group name */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: isFirst ? 32 : isTop3 ? 26 : 22,
                      fontWeight: 600,
                      color: G.text,
                      marginBottom: 4
                    }}>
                      {r.group_name}
                    </div>
                    {/* Progress bar */}
                    <div style={{
                      background: G.bg,
                      borderRadius: 999,
                      height: 8,
                      overflow: "hidden",
                      position: "relative"
                    }}>
                      <div style={{
                        background: `linear-gradient(90deg, ${isFirst ? G.gold : isTop3 ? G.goldDim : G.accent} 0%, ${isFirst ? G.gold + "88" : isTop3 ? G.goldDim + "88" : G.accent + "88"} 100%)`,
                        height: "100%",
                        width: `${percentage}%`,
                        transition: "width 0.5s ease",
                        borderRadius: 999
                      }} />
                    </div>
                  </div>

                  {/* Score */}
                  <div style={{ textAlign: "right" }}>
                    <div style={{
                      fontSize: isFirst ? 42 : isTop3 ? 36 : 30,
                      fontWeight: 700,
                      color: isFirst ? G.gold : isTop3 ? G.goldDim : G.text,
                      fontFamily: "'DM Mono',monospace",
                      lineHeight: 1
                    }}>
                      {r.total}
                    </div>
                    <div style={{
                      fontSize: 14,
                      color: G.muted,
                      marginTop: 4,
                      fontFamily: "'DM Mono',monospace"
                    }}>
                      / {maxScore}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer med auto-refresh indikator */}
        <div style={{
          textAlign: "center",
          marginTop: 40,
          fontSize: 14,
          color: G.muted,
          fontFamily: "'DM Mono',monospace"
        }}>
          {t.updatingLive}{lastUpdate ? ` · ${new Date(lastUpdate).toLocaleTimeString()}` : ""}
        </div>
      </div>
    </div>
  );
}

export { LeaderboardScreen };
